import { useEffect, useRef, useState } from "react";

const TILE = 100;
const GRID_W = 20;
const GRID_H = 12;

type Enemy = {
    id: number;
    x: number;
    y: number;
    pathIndex: number;
    hp: number;
    speed: number;
};

type Tower = {
    id: number;
    x: number;
    y: number;
    damage: number;
    range: number;
    fireRate: number;
    cooldown: number;
    level: number;
};

type Bullet = {
    id: number;
    x: number;
    y: number;
    speed: number;
    damage: number;
    targetId: number;
};

const TowerGame = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [money, setMoney] = useState(200);
    const [lives, setLives] = useState(10);
    const [wave, setWave] = useState(0);
    const [towers, setTowers] = useState<Tower[]>([]);
    const enemiesRef = useRef<Enemy[]>([]);
    const towersRef = useRef<Tower[]>([]);
    const bulletsRef = useRef<Bullet[]>([]);
    const [selectedTower, setSelectedTower] = useState<Tower | null>(null);
    const path: [number, number][] = [ [0, 5], [6, 5], [6, 2], [12, 2], [12, 9], [18, 9], [18, 4], [19, 4], ];

const startWave = () => {
    setWave((w) => w + 1);
    const newEnemies: Enemy[] = Array.from({ length: 10 }, (_, i) => ({
        id: Date.now() + i,
        x: path[0][0] * TILE + TILE / 2,
        y: path[0][1] * TILE + TILE / 2,
        pathIndex: 0,
        hp: 30 + wave * 15,
        speed: 40,
    }));
    enemiesRef.current.push(...newEnemies); // update game loop data
};

  // place or select tower
const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / TILE);
    const y = Math.floor((e.clientY - rect.top) / TILE);

    const clickedTower = towers.find((t) => t.x === x && t.y === y);
    if(clickedTower) {
        setSelectedTower(clickedTower);
        return;
    }

    if(money >= 50) {
        const newTower: Tower = {
            id: Date.now(),
            x,
            y,
            damage: 10,
            range: 100,
            fireRate: 1,
            cooldown: 0,
            level: 1,
        };
        towersRef.current.push(newTower);
        setTowers((t) => [...t, newTower]);
        setMoney((m) => m - 50);
    }
    };

  // upgrade tower
    const upgradeTower = () => {
        if (!selectedTower) return;
        if (money < 100) return;

        setTowers((ts) =>
        ts.map((t) => t.id === selectedTower.id ? { ...t, level: t.level + 1, damage: t.damage + 5, range: t.range + 20, fireRate: t.fireRate + 0.3, } : t )
        );
        towersRef.current = towersRef.current.map((t) =>
        t.id === selectedTower.id
            ? {
                ...t,
                level: t.level + 1,
                damage: t.damage + 5,
                range: t.range + 20,
                fireRate: t.fireRate + 0.3,
            }
            : t
        );
        setMoney((m) => m - 100);
        setSelectedTower(null);
    };

useEffect(() => {
    const canvas = canvasRef.current!;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if(ctx === null) return;

    let last = performance.now();
    if(ctx !== null){
        console.log("Game loop started");
    const gameLoop = (ts: number) => {
        const dt = (ts - last) / 1000;
        last = ts;

        // clear
        ctx.fillStyle = "#082038";
        ctx.fillRect(0, 0, GRID_W * TILE, GRID_H * TILE);

        // draw path
        ctx.strokeStyle = "#444";
        ctx.lineWidth = TILE;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(path[0][0] * TILE + TILE / 2, path[0][1] * TILE + TILE / 2);
        for (let i = 1; i < path.length; i++) {
            ctx.lineTo(path[i][0] * TILE + TILE / 2, path[i][1] * TILE + TILE / 2);
        }
        ctx.stroke();

        // === UPDATE ENEMIES ===
        enemiesRef.current = enemiesRef.current
        .map((enemy) => {
            const [tx, ty] = path[enemy.pathIndex + 1] || path[enemy.pathIndex];
            const targetX = tx * TILE + TILE / 2;
            const targetY = ty * TILE + TILE / 2;
            const dx = targetX - enemy.x;
            const dy = targetY - enemy.y;
            const dist = Math.hypot(dx, dy);

            if (dist < enemy.speed * dt) {
            enemy.x = targetX;
            enemy.y = targetY;
            enemy.pathIndex++;
            if (enemy.pathIndex >= path.length - 1) {
                setLives((l) => l - 1); // update state just for UI
                enemy.hp = 0;
            }
            } else {
            enemy.x += (dx / dist) * enemy.speed * dt;
            enemy.y += (dy / dist) * enemy.speed * dt;
            }
            return enemy;
        })
        .filter((e) => e.hp > 0);

        // === UPDATE TOWERS (shooting) ===
        towersRef.current.forEach((tower) => {
        tower.cooldown -= dt;
        if (tower.cooldown <= 0) {
            const target = enemiesRef.current.find((e) => {
            const d = Math.hypot(
                e.x - (tower.x * TILE + TILE / 2),
                e.y - (tower.y * TILE + TILE / 2)
            );
            return d <= tower.range;
            });
            if (target) {
            bulletsRef.current.push({
                id: Date.now() + Math.random(),
                x: tower.x * TILE + TILE / 2,
                y: tower.y * TILE + TILE / 2,
                speed: 200,
                damage: tower.damage,
                targetId: target.id,
            });
            tower.cooldown = 1 / tower.fireRate;
            }
        }
        });

        // === UPDATE BULLETS ===
        bulletsRef.current = bulletsRef.current
        .map((b) => {
            const target = enemiesRef.current.find((e) => e.id === b.targetId);
            if (!target) return null;
            const dx = target.x - b.x;
            const dy = target.y - b.y;
            const dist = Math.hypot(dx, dy);
            if (dist < 5) {
            target.hp -= b.damage;
            if (target.hp <= 0) setMoney((m) => m + 20);
            return null;
            }
            b.x += (dx / dist) * b.speed * dt;
            b.y += (dy / dist) * b.speed * dt;
            return b;
        })
        .filter((b): b is Bullet => Boolean(b));

        // === DRAW ENEMIES ===
        enemiesRef.current.forEach((e) => {
            ctx.fillStyle = "crimson";
            ctx.beginPath();
            ctx.arc(e.x, e.y, 12, 0, Math.PI * 2);
            ctx.fill();
        });

        // === DRAW TOWERS ===
        towersRef.current.forEach((t) => {
            ctx.fillStyle = "green";
            ctx.fillRect(t.x * TILE + 4, t.y * TILE + 4, TILE - 8, TILE - 8);
        });

        // === DRAW BULLETS ===
        bulletsRef.current.forEach((b) => {
            ctx.fillStyle = "yellow";
            ctx.beginPath();
            ctx.arc(b.x, b.y, 4, 0, Math.PI * 2);
            ctx.fill();
        });

        requestAnimationFrame(gameLoop);
    }

    requestAnimationFrame(gameLoop);
    }
}, []);
  return (
    <div>
      <div className="ui">
        <span>💰 {money}</span>
        <span>❤️ {lives}</span>
        <span>🌊 {wave}</span>
        <button onClick={startWave}>Start Wave</button>
        {selectedTower && (
          <button onClick={upgradeTower}>Upgrade Tower (100)</button>
        )}
      </div>
      <canvas ref={canvasRef} width={GRID_W * TILE} height={GRID_H * TILE} onClick={handleClick} style={{ border: "2px solid #0b324f", cursor: "pointer" }} />
    </div>
  );
}

export default TowerGame
